import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Guest } from '../types';

// Backward-compat: old links use ?guestId= / ?token= query params.
const getGuestIdFromQuery = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('guestId') || params.get('token') || undefined;
};

const InvitePage = () => {
  // New links put the id in the path (/suha-and-hasan/:guestId); fall back to query.
  const { guestId: pathGuestId } = useParams();
  const guestId = useMemo(
    () => pathGuestId || getGuestIdFromQuery(),
    [pathGuestId]
  );
  const [guest, setGuest] = useState<Guest | null>(null);
  const [guestCount, setGuestCount] = useState(1);
  const [loadingGuest, setLoadingGuest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');
  const [videoEnded, setVideoEnded] = useState(false);
  const [needsInteraction, setNeedsInteraction] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!guestId) return;

    const loadGuest = async () => {
      setLoadingGuest(true);
      setError('');
      try {
        const guestRef = doc(db, 'guests', guestId);
        const snapshot = await getDoc(guestRef);

        if (!snapshot.exists()) {
          setError('Guest record not found.');
          setGuest(null);
          return;
        }

        const data = snapshot.data();
        const guestData = {
          id: snapshot.id,
          name: data.name || 'Guest',
          email: data.email || '',
          phone: data.phone || '',
          invitedGuests: data.invitedGuests || 1,
          rsvpStatus: data.rsvpStatus || 'pending',
          comingCount: data.comingCount || 0,
          respondedAt: data.respondedAt || undefined,
          inviteToken: data.inviteToken || undefined,
          notes: data.notes || '',
        } as Guest;

        setGuest(guestData);
        setNotes(guestData.notes || '');
        // Seed count from prior response if they've already replied, otherwise default to full party
        setGuestCount(
          guestData.rsvpStatus !== 'pending'
            ? Math.max(1, guestData.comingCount)
            : Math.max(1, guestData.invitedGuests)
        );

        // Record first open — only write if not already marked
        if (!data.inviteOpened) {
          await updateDoc(guestRef, {
            inviteOpened: true,
            inviteOpenedAt: serverTimestamp(),
          });
        }
      } catch (e) {
        console.error(e);
        setError('Unable to load guest information.');
      } finally {
        setLoadingGuest(false);
      }
    };

    loadGuest();
  }, [guestId]);

  const handleRsvp = async (response: 'yes' | 'no') => {
    if (!guest) return;

    setSubmitting(true);
    setError('');

    try {
      const guestRef = doc(db, 'guests', guest.id);
      const updateData = {
        rsvpStatus: response,
        comingCount: response === 'yes' ? guestCount : 0,
        respondedAt: new Date().toISOString(),
        notes: notes.trim(),
      };

      await updateDoc(guestRef, updateData);
      setGuest({ ...guest, ...updateData });
      setEditing(false);
    } catch (e) {
      console.error(e);
      setError('Unable to submit your RSVP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = () => {
    if (guest) {
      setGuestCount(Math.max(1, guest.comingCount));
      setNotes(guest.notes || '');
    }
    setEditing(true);
  };

  const handleCancelEdit = () => {
    if (guest) {
      setGuestCount(Math.max(1, guest.comingCount));
      setNotes(guest.notes || '');
    }
    setEditing(false);
  };

  // No autoplay — every user sees the "Tap to view" screen first.

  const handlePlayClick = () => {
    videoRef.current?.play();
    setNeedsInteraction(false);
  };

  const invitedGuests = guest?.invitedGuests ?? 1;
  const hasResponded = guest !== null && guest.rsvpStatus !== 'pending';

  const renderForm = () => {
    if (!guestId) {
      return <div className="rsvp-title">Please open the invite using your personal link.</div>;
    }
    if (loadingGuest) {
      return <div className="rsvp-title">Loading your invitation...</div>;
    }
    if (error) {
      return <div className="rsvp-title">{error}</div>;
    }

    // Confirmed view — show their saved response with an edit option
    if (hasResponded && !editing) {
      return (
        <>
          <div className="rsvp-title">
            {guest!.rsvpStatus === 'yes'
              ? `${guest!.comingCount} ${guest!.comingCount === 1 ? 'guest' : 'guests'} will be attending.`
              : 'You have declined.'}
          </div>
          <div className="rsvp-thankyou">Thank you for your response.</div>
          <div className="rsvp-buttons" style={{ marginTop: '1.25rem' }}>
            <button className="rsvp-btn no" onClick={handleEditClick}>
              Edit response
            </button>
          </div>
        </>
      );
    }

    // First-time or edit view
    return (
      <>
        {guest?.name && (
          <div className="rsvp-guest-name">{guest.name}</div>
        )}
        <div className="rsvp-title">Will you be attending?</div>
        {invitedGuests > 1 && (
          <>
            <div className="rsvp-offer">
              Kindly indicate how many from your party will be attending.
            </div>
            <div className="rsvp-stepper">
              <button
                className="rsvp-stepper-btn"
                onClick={() => setGuestCount(c => Math.max(1, c - 1))}
                disabled={submitting || guestCount <= 1}
                aria-label="Decrease"
              >−</button>
              <div className="rsvp-stepper-count-wrapper">
                <span className="rsvp-stepper-count">{guestCount}</span>
                <span className="rsvp-stepper-of">of {invitedGuests}</span>
              </div>
              <button
                className="rsvp-stepper-btn"
                onClick={() => setGuestCount(c => Math.min(invitedGuests, c + 1))}
                disabled={submitting || guestCount >= invitedGuests}
                aria-label="Increase"
              >+</button>
            </div>
          </>
        )}
        <div className="rsvp-notes-wrapper">
          <textarea
            className="rsvp-notes"
            placeholder="Any notes or comments? (optional)"
            maxLength={200}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            disabled={submitting}
          />
          <div className="rsvp-notes-count">{notes.length} / 200</div>
        </div>
        <div className="rsvp-buttons">
          <button
            className="rsvp-btn yes"
            onClick={() => handleRsvp('yes')}
            disabled={submitting || guestCount < 1 || guestCount > invitedGuests}
          >
            Yes
          </button>
          <button
            className="rsvp-btn no"
            onClick={() => handleRsvp('no')}
            disabled={submitting}
          >
            No
          </button>
        </div>
        {editing && (
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button className="rsvp-cancel-btn" onClick={handleCancelEdit}>
              Cancel
            </button>
          </div>
        )}
        {error && <div className="rsvp-error">{error}</div>}
      </>
    );
  };

  return (
    <div className="invite-background">
      {!guestId && (
        <Link
          to="/dashboard"
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 10,
            background: '#bfa14a',
            color: '#fff',
            fontFamily: "'Playfair Display', serif",
            fontSize: '0.9rem',
            fontWeight: 600,
            padding: '0.5em 1.25em',
            borderRadius: '8px',
            textDecoration: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          Dashboard
        </Link>
      )}
      <div className="invite-content">
        <div className="invite-hero">
          <div className="invite-card-wrapper">
            <video
              ref={videoRef}
              src="/invite-card.mp4"
              playsInline
              onEnded={() => setVideoEnded(true)}
              onError={() => setVideoEnded(true)}
              className="invite-card"
            />
          </div>
          {needsInteraction && !loadingGuest && (
            <div className="video-play-overlay">
              {guest?.name && (
                <div className="video-play-name">{guest.name}</div>
              )}
              <button className="video-play-btn" onClick={handlePlayClick}>
                Tap to view your invitation
              </button>
            </div>
          )}
        </div>
        {videoEnded && (
          <div className="rsvp-form rsvp-form--fadein">
            {renderForm()}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitePage;
